import React from 'react'
import { Card, IconTooltip, cn } from '@duck/ui'

interface KpiStatCardProps {
  label: string
  value: string | number
  sublabel?: string
  tooltip?: string
  /**
   * This metric's share (0–1) of its natural whole — a segment against total
   * users, a funnel stage against all candidates, or a rate against itself.
   * When set, the tile draws a proportion gauge so the number reads as part of
   * something, not in isolation. Omit for standalone counts.
   */
  fraction?: number
  /** Anchor metric — brightened surface and a solid gauge to lead the eye. */
  emphasis?: boolean
  /** Makes the card an interactive click target (keyboard accessible). */
  onClick?: () => void
}

export const KpiStatCard: React.FC<KpiStatCardProps> = ({
  label,
  value,
  sublabel,
  tooltip,
  fraction,
  emphasis = false,
  onClick,
}) => {
  const pct =
    fraction === undefined
      ? undefined
      : Math.min(100, Math.max(0, fraction * 100))

  const interactiveProps =
    onClick !== undefined
      ? {
          role: 'button' as const,
          tabIndex: 0,
          onClick,
          onKeyDown: (e: React.KeyboardEvent) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              onClick()
            }
          },
        }
      : {}

  return (
    <Card
      {...interactiveProps}
      className={cn(
        'group flex flex-col gap-1 p-4 transition-all duration-200',
        'hover:-translate-y-0.5 hover:border-accent/50 hover:shadow-md',
        emphasis && 'border-accent/40 bg-accent/[0.04]',
        onClick !== undefined && 'cursor-pointer'
      )}
    >
      <span className="flex items-center gap-1 text-sm text-muted-foreground">
        {label}
        {tooltip !== undefined && (
          <IconTooltip content={tooltip} tooltipClassName="max-w-[240px]" />
        )}
      </span>

      <span className="font-jet-mono text-3xl font-semibold leading-none tabular-nums text-foreground">
        {value}
      </span>

      {sublabel !== undefined && (
        <span className="text-xs text-muted-foreground">{sublabel}</span>
      )}

      {pct !== undefined && (
        <div className="mt-2 h-1 w-full overflow-hidden rounded-full bg-muted/20">
          <div
            className={cn(
              'h-full rounded-full transition-[width,background-color] duration-500',
              emphasis ? 'bg-accent' : 'bg-accent/70 group-hover:bg-accent'
            )}
            style={{ width: `${pct}%` }}
          />
        </div>
      )}
    </Card>
  )
}
